"use client";

import { useState, type ReactNode } from "react";
import {
  Brain,
  Globe,
  Handshake,
  Rocket,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { Footer } from "../_components/footer";
import { Header } from "../_components/header";

type RoleId = "senior-ai" | "junior" | "marketing";

type WhyCard = {
  icon: LucideIcon;
  name: string;
  desc: string;
};

const WHY: WhyCard[] = [
  {
    icon: Globe,
    name: "Fully Remote",
    desc: "Work from anywhere. We have team members across multiple timezones. We care about output, not office hours.",
  },
  {
    icon: Rocket,
    name: "Early Stage, Big Upside",
    desc: "We're pre-seed and growing fast. Early team members get to shape the product, the culture, and the company direction.",
  },
  {
    icon: Brain,
    name: "Cutting-Edge AI",
    desc: "You'll work with cutting-edge language models, multi-model routing, voice AI, and agent frameworks every single day. This isn't a maintenance job.",
  },
  {
    icon: Zap,
    name: "Ship Fast",
    desc: "No bureaucracy. No 6-month roadmaps. You build something, it goes live. Ideas in the morning become features by the afternoon.",
  },
  {
    icon: Wallet,
    name: "Competitive Pay",
    desc: "Fair compensation with room to grow as the company scales. We're building toward a raise and we want the team that got us there to benefit.",
  },
  {
    icon: Handshake,
    name: "Small Team, Big Impact",
    desc: "You won't be a cog in a machine. Every person here directly shapes the product and the business. Your work matters from day one.",
  },
];

// Ported from raileon-careers.html. The role-card open/close toggling
// the original did via classList manipulation is now a simple state
// set — which roles are in the `open` Set. The Senior AI role defaults
// to open (matches the original `role-card open` class on first
// render).
export function CareersContent() {
  const [open, setOpen] = useState<Set<RoleId>>(new Set(["senior-ai"]));

  function toggle(id: RoleId) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <main id="main">
      <Header />
      <div className="raileon-careers">
        <CareersHero />
        <WhyWorkHere />

        {/* OPEN ROLES */}
        <div className="roles-section">
          <div className="roles-title">Open Positions</div>

          <RoleCard
            id="senior-ai"
            name="Senior AI / LLM Developer"
            tags={["Full-Time", "Engineering"]}
            applySubject="Application: Senior AI Developer"
            open={open.has("senior-ai")}
            onToggle={toggle}
          >
            <SeniorAiRoleBody />
          </RoleCard>

          <RoleCard
            id="junior"
            name="Junior Developer"
            tags={["Full-Time", "Engineering"]}
            applySubject="Application: Junior Developer"
            open={open.has("junior")}
            onToggle={toggle}
          >
            <JuniorRoleBody />
          </RoleCard>

          <RoleCard
            id="marketing"
            name="Marketing & Content Specialist"
            tags={["Part-Time / Contract", "Marketing"]}
            applySubject="Application: Marketing Specialist"
            open={open.has("marketing")}
            onToggle={toggle}
          >
            <MarketingRoleBody />
          </RoleCard>
        </div>

        <GeneralApplication />
        <CareersClosing />
      </div>

      <style>{careersCss}</style>
      <Footer />
    </main>
  );
}

function CareersHero() {
  return (
    <div className="careers-hero">
      <div className="hero-tag">Careers</div>
      <h1 className="hero-title">
        Build the Future of
        <br />
        Business Automation
      </h1>
      <div className="hero-sub">
        We&apos;re a small, fast-moving team building AI agents that run
        businesses while their owners sleep. If you want to work on
        something that matters, you&apos;re in the right place.
      </div>
    </div>
  );
}

function WhyWorkHere() {
  return (
    <div className="why-section">
      <div className="why-grid">
        {WHY.map((w) => {
          const Icon = w.icon;
          return (
            <div key={w.name} className="why-card">
              <div className="why-icon">
                <Icon size={22} aria-hidden />
              </div>
              <div className="why-name">{w.name}</div>
              <div className="why-desc">{w.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface RoleCardProps {
  id: RoleId;
  name: string;
  tags: string[];
  applySubject: string;
  open: boolean;
  onToggle: (id: RoleId) => void;
  children: ReactNode;
}

function RoleCard({
  id,
  name,
  tags,
  applySubject,
  open,
  onToggle,
  children,
}: RoleCardProps) {
  return (
    <div className={`role-card${open ? " open" : ""}`}>
      <button
        type="button"
        className="role-header"
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        <div className="role-header-left">
          <div className="role-name">{name}</div>
          <div className="role-meta">
            <span className="role-tag">
              <span className="dot" /> Remote
            </span>
            {tags.map((t) => (
              <span key={t} className="role-tag">
                {t}
              </span>
            ))}
          </div>
        </div>
        <span className="role-arrow">▾</span>
      </button>
      <div className="role-body">
        <div className="role-body-inner">
          {children}
          <a
            className="role-apply-btn"
            href={`mailto:careers@raileon.com?subject=${applySubject}`}
          >
            Apply Now →
          </a>
        </div>
      </div>
    </div>
  );
}

function SeniorAiRoleBody() {
  return (
    <>
      <div className="role-section-title">About the Role</div>
      <p className="role-desc">
        We&apos;re looking for a senior developer with hands-on
        experience building AI agent systems. You&apos;ll work
        directly with our Lead Developer to build, deploy, and
        manage AI agents that run real business operations for our
        clients. This isn&apos;t a research role; you&apos;ll be
        shipping production code that impacts businesses every day.
      </p>

      <div className="role-section-title">What You&apos;ll Do</div>
      <ul className="role-list">
        <li>
          Build and optimize AI agents using our internal framework
          with frontier LLM APIs
        </li>
        <li>
          Design multi-model routing logic for cost and performance
          optimization
        </li>
        <li>
          Develop and maintain per-client VPS deployments with Docker
          isolation
        </li>
        <li>
          Build integrations with third-party APIs: CRMs, email,
          social media, voice, scheduling
        </li>
        <li>Implement prompt injection defense and security hardening</li>
        <li>
          Optimize token usage and reduce API costs without
          sacrificing output quality
        </li>
        <li>
          Collaborate with the team on new agent capabilities: voice
          calling, knowledge base learning, automated reporting
        </li>
      </ul>

      <div className="role-section-title">
        What We&apos;re Looking For
      </div>
      <ul className="role-list">
        <li>
          3+ years of backend development experience (Python or
          Node.js)
        </li>
        <li>Hands-on experience with LLM APIs</li>
        <li>
          Experience building agent-based systems or multi-step AI
          workflows
        </li>
        <li>
          Familiarity with Docker, Linux VPS management, and
          deployment pipelines
        </li>
        <li>
          Strong understanding of prompt engineering and context
          management
        </li>
        <li>Experience with API integrations (REST, OAuth, webhooks)</li>
        <li>
          Self-directed and comfortable working in a fast-paced,
          early-stage environment
        </li>
      </ul>

      <div className="role-section-title">Bonus Points</div>
      <ul className="role-list">
        <li>
          Significant experience with modern agent frameworks
        </li>
        <li>
          Experience with voice AI (ElevenLabs, Twilio, similar)
        </li>
        <li>Experience with vector databases and RAG pipelines</li>
        <li>Background in SaaS or managed services</li>
      </ul>
    </>
  );
}

function JuniorRoleBody() {
  return (
    <>
      <div className="role-section-title">About the Role</div>
      <p className="role-desc">
        We&apos;re looking for a hungry junior developer who wants to
        learn AI development by doing it. You&apos;ll work alongside
        our senior team on real client deployments, building
        integrations, writing agent logic, and maintaining
        infrastructure. This is a learn-fast, ship-fast environment.
      </p>

      <div className="role-section-title">What You&apos;ll Do</div>
      <ul className="role-list">
        <li>
          Build and test API integrations with client tools (CRMs,
          email, social platforms)
        </li>
        <li>Assist with agent deployment, monitoring, and debugging</li>
        <li>
          Write and refine prompts for client-specific agent
          behaviors
        </li>
        <li>Help maintain documentation and internal knowledge bases</li>
        <li>
          Participate in code reviews and learn from senior developers
          daily
        </li>
      </ul>

      <div className="role-section-title">
        What We&apos;re Looking For
      </div>
      <ul className="role-list">
        <li>Basic proficiency in Python or JavaScript</li>
        <li>Familiarity with APIs and how web services communicate</li>
        <li>Interest in AI, automation, and agent-based systems</li>
        <li>
          Strong work ethic and willingness to figure things out
          independently
        </li>
        <li>
          Good communication skills; you&apos;ll be working async
          across timezones
        </li>
      </ul>
    </>
  );
}

function MarketingRoleBody() {
  return (
    <>
      <div className="role-section-title">About the Role</div>
      <p className="role-desc">
        We need someone who can create content that makes business
        owners stop scrolling. You&apos;ll handle social media
        content, blog posts, short-form video scripts, and ad copy
        across Instagram, TikTok, Twitter/X, and LinkedIn. You should
        understand the AI space and be able to translate technical
        capabilities into benefits that non-technical people care
        about.
      </p>

      <div className="role-section-title">What You&apos;ll Do</div>
      <ul className="role-list">
        <li>Create and schedule social media content across all platforms</li>
        <li>Write blog posts optimized for SEO targeting small business owners</li>
        <li>Write scripts for short-form video content (Reels, TikToks)</li>
        <li>Help develop ad copy for Google and Meta campaigns</li>
        <li>
          Manage community engagement: respond to comments and DMs
        </li>
      </ul>

      <div className="role-section-title">
        What We&apos;re Looking For
      </div>
      <ul className="role-list">
        <li>Portfolio of social media or content marketing work</li>
        <li>Strong writing skills: punchy, clear, no fluff</li>
        <li>
          Understanding of AI and automation (doesn&apos;t need to
          be technical)
        </li>
        <li>Experience with social media scheduling tools</li>
        <li>
          Ability to create content that drives engagement, not just
          impressions
        </li>
      </ul>
    </>
  );
}

function GeneralApplication() {
  return (
    <div className="general-section">
      <div className="general-card">
        <div className="general-title">Don&apos;t See Your Role?</div>
        <div className="general-desc">
          We&apos;re always looking for talented people who are excited
          about AI and want to build something that matters. Send us your
          resume and tell us what you&apos;re great at.
        </div>
        <a
          className="general-email"
          href="mailto:careers@raileon.com?subject=General Application"
        >
          careers@raileon.com
        </a>
      </div>
    </div>
  );
}

function CareersClosing() {
  return (
    <div className="careers-cta">
      <div className="cta-divider" />
      <div className="cta-quote">
        &ldquo;We&apos;re building a parachute after jumping out of the
        airplane. <span>Come jump with us.</span>&rdquo;
      </div>
      <div className="cta-note">The Raileon Team, Miami</div>
    </div>
  );
}

const careersCss = `
.raileon-careers { background: #04050A; color: #F1F3F5; }
.raileon-careers * { box-sizing: border-box; }

.raileon-careers .careers-hero {
  max-width: 760px;
  margin: 0 auto;
  padding: 100px 24px 60px;
  text-align: center;
}
.raileon-careers .hero-tag {
  font-size: 12px;
  font-weight: 700;
  color: #10B981;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 14px;
}
.raileon-careers .hero-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 900;
  line-height: 1.12;
  margin-bottom: 16px;
}
.raileon-careers .hero-sub {
  font-size: 16px;
  color: #8B8FA3;
  line-height: 1.7;
  max-width: 560px;
  margin: 0 auto;
}

.raileon-careers .why-section {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px 70px;
}
.raileon-careers .why-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.raileon-careers .why-card {
  background: #0A0C14;
  border: 1px solid #1A1D2B;
  border-radius: 14px;
  padding: 24px;
  transition: border-color 0.3s;
}
.raileon-careers .why-card:hover { border-color: rgba(16,185,129,0.3); }
.raileon-careers .why-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: color-mix(in oklab, var(--rl-accent) 10%, transparent);
  color: var(--rl-accent);
  margin-bottom: 14px;
}
.raileon-careers .why-name { font-size: 15px; font-weight: 800; margin-bottom: 6px; }
.raileon-careers .why-desc { font-size: 12px; color: #8B8FA3; line-height: 1.6; }

.raileon-careers .roles-section {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px 70px;
}
.raileon-careers .roles-title {
  font-size: 12px;
  font-weight: 700;
  color: #10B981;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 28px;
  text-align: center;
}

.raileon-careers .role-card {
  background: #0A0C14;
  border: 1px solid #1A1D2B;
  border-radius: 14px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: border-color 0.3s;
}
.raileon-careers .role-card:hover { border-color: rgba(16,185,129,0.3); }

.raileon-careers .role-header {
  padding: 24px 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  text-align: left;
}
.raileon-careers .role-name {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 4px;
}
.raileon-careers .role-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.raileon-careers .role-tag {
  font-size: 11px;
  font-weight: 600;
  color: #474B5F;
  display: flex;
  align-items: center;
  gap: 4px;
}
.raileon-careers .role-tag .dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #10B981;
}
.raileon-careers .role-arrow {
  font-size: 18px;
  color: #474B5F;
  transition: transform 0.3s, color 0.3s;
}
.raileon-careers .role-card.open .role-arrow {
  transform: rotate(180deg);
  color: #10B981;
}

.raileon-careers .role-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
}
.raileon-careers .role-card.open .role-body { max-height: 1600px; }
.raileon-careers .role-body-inner {
  padding: 24px 28px 28px;
  border-top: 1px solid #1A1D2B;
}
.raileon-careers .role-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #F1F3F5;
  margin-bottom: 8px;
  margin-top: 18px;
}
.raileon-careers .role-section-title:first-child { margin-top: 0; }
.raileon-careers .role-desc {
  font-size: 13px;
  color: #8B8FA3;
  line-height: 1.7;
  margin-bottom: 8px;
}
.raileon-careers .role-list {
  list-style: none;
  padding: 0;
}
.raileon-careers .role-list li {
  font-size: 13px;
  color: #8B8FA3;
  line-height: 1.6;
  padding-left: 16px;
  position: relative;
  margin-bottom: 4px;
}
.raileon-careers .role-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #10B981;
}
.raileon-careers .role-apply-btn {
  display: inline-block;
  margin-top: 20px;
  padding: 12px 32px;
  background: #10B981;
  color: #04050A;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
}
.raileon-careers .role-apply-btn:hover { background: #059669; }

.raileon-careers .general-section {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px 80px;
}
.raileon-careers .general-card {
  background: #0A0C14;
  border: 1px solid #1A1D2B;
  border-radius: 14px;
  padding: 36px;
  text-align: center;
}
.raileon-careers .general-title {
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 8px;
}
.raileon-careers .general-desc {
  font-size: 14px;
  color: #8B8FA3;
  line-height: 1.7;
  max-width: 480px;
  margin: 0 auto 24px;
}
.raileon-careers .general-email {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: #0E1019;
  border: 1px solid #1A1D2B;
  border-radius: 10px;
  color: #10B981;
  font-size: 15px;
  font-weight: 700;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
  text-decoration: none;
  transition: border-color 0.2s;
}
.raileon-careers .general-email:hover { border-color: #10B981; }

.raileon-careers .careers-cta {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px 100px;
  text-align: center;
}
.raileon-careers .cta-divider {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #10B981, #06B6D4);
  border-radius: 3px;
  margin: 0 auto 30px;
}
.raileon-careers .cta-quote {
  font-size: clamp(18px, 3vw, 24px);
  font-weight: 700;
  line-height: 1.5;
  color: #F1F3F5;
  margin-bottom: 8px;
}
.raileon-careers .cta-quote span { color: #10B981; }
.raileon-careers .cta-note {
  font-size: 13px;
  color: #474B5F;
}

@media (max-width: 700px) {
  .raileon-careers .why-grid { grid-template-columns: 1fr; }
  .raileon-careers .careers-hero { padding: 60px 16px 40px; }
  .raileon-careers .role-header { padding: 20px; }
  .raileon-careers .role-body-inner { padding: 20px; }
  .raileon-careers .role-meta { gap: 10px; }
}
`;
